import React, { useState } from "react";
import "./Summary.css";
import iconCart from "../assets/images/icon-cart.svg";
import iconBox from "../assets/images/icon-box.svg";
import iconBoxCheck from "../assets/images/icon-box-check.svg";
import iconTrash from "../assets/images/icon-trash.svg";
import iconMinus from "../assets/images/icon-minus.svg";
import iconPlus from "../assets/images/Icon-plus.svg";
import iconX from "../assets/images/icon-x.svg";

const MEAL_PRICE = 14.95;
const DISCOUNT = 20;
const DELIVERY_FEE = 7.99;
const TOTAL_SLOTS = 6;

function formatPrice(amount) {
  return amount.toFixed(2).replace(".", ",") + " $";
}

// Group a meal's selections into unique (side, size) combos with counts
function getGroups(mealId, mealQuantities, mealSelections) {
  const selections = mealSelections[mealId] || [];
  if (!selections.length) {
    // No selections yet — show one unfilled row
    const qty = mealQuantities[mealId] || 0;
    return qty > 0 ? [{ side: "", size: "Régulier", count: qty }] : [];
  }
  const map = {};
  selections.forEach(({ side, size }) => {
    const key = `${size}||${side}`;
    if (!map[key]) map[key] = { side, size, count: 0 };
    map[key].count++;
  });
  return Object.values(map);
}

export default function Summary({
  isOpen,
  onClose,
  meals = [],
  mealQuantities = {},
  mealSelections = {},
  onIncrementCombo,
  onDecrementCombo,
  onConfirm,
}) {
  if (!isOpen) return null;

  const addedMeals = meals.filter((m) => (mealQuantities[m.id] || 0) > 0);
  const mealsCount = Object.values(mealQuantities).reduce((s, q) => s + q, 0);

  // All (meal, group) pairs, split by format
  const regularRows = [];
  const familyRows = [];
  addedMeals.forEach((meal) => {
    getGroups(meal.id, mealQuantities, mealSelections).forEach((group) => {
      if (group.size === "Familial") familyRows.push({ meal, group });
      else regularRows.push({ meal, group });
    });
  });

  const subtotal = mealsCount * MEAL_PRICE;
  const total = Math.max(0, subtotal - DISCOUNT) + DELIVERY_FEE;
  const isComplete = mealsCount >= TOTAL_SLOTS;

  return (
    <>
      <div className="summary-backdrop" onClick={onClose} />
      <div className="summary">
        {/* Header */}
        <div className="summary__header">
          <div className="summary__cart-icon">
            <img src={iconCart} alt="Panier" />
          </div>
          <p className="summary__title">Ma commande</p>
          <button
            className="summary__close"
            onClick={onClose}
            aria-label="Fermer"
          >
            <img src={iconX} alt="" />
          </button>
        </div>

        {/* Delivery row */}
        <div className="summary__delivery">
          <div className="summary__delivery-icons">
            <img
              src={iconBoxCheck}
              alt=""
              className="summary__icon-box-check"
            />
            <img
              src={iconBoxCheck}
              alt=""
              className="summary__icon-box-check"
            />
          </div>
          <div className="summary__delivery-text">
            <p className="summary__delivery-label">Livraison du jeudi</p>
            <p className="summary__delivery-date">16 novembre 2023</p>
          </div>
          <button className="summary__modify">Modifier</button>
        </div>

        {/* Meal list */}
        <div className="summary__section">
          <p className="summary__section-title">Repas</p>

          {addedMeals.length === 0 && (
            <p className="summary__empty">Aucun repas ajouté.</p>
          )}

          {regularRows.map(({ meal, group }) => (
            <div
              className="summary__meal"
              key={`${meal.id}-${group.size}-${group.side}`}
            >
              <img
                src={meal.image}
                alt={meal.title}
                className="summary__meal-img"
              />
              <div className="summary__meal-info">
                <p className="summary__meal-title">{meal.title}</p>
                {group.side && (
                  <p className="summary__meal-side">{group.side}</p>
                )}
              </div>
              <p className="summary__meal-price">
                {formatPrice(MEAL_PRICE * group.count)}
              </p>
              <div className="summary__stepper">
                <button
                  className="summary__stepper-btn"
                  onClick={() =>
                    onDecrementCombo(meal.id, group.side, group.size)
                  }
                  aria-label="Retirer"
                >
                  <img src={group.count > 1 ? iconMinus : iconTrash} alt="" />
                </button>
                <span className="summary__stepper-qty">{group.count}</span>
                <button
                  className="summary__stepper-btn"
                  onClick={() =>
                    onIncrementCombo(meal.id, group.side, group.size)
                  }
                  aria-label="Ajouter"
                >
                  <img src={iconPlus} alt="" />
                </button>
              </div>
            </div>
          ))}

          {familyRows.length > 0 && (
            <p className="summary__section-title summary__section-title--sub">
              Format familial
            </p>
          )}

          {familyRows.map(({ meal, group }) => (
            <div
              className="summary__meal"
              key={`${meal.id}-${group.size}-${group.side}`}
            >
              <img
                src={meal.image}
                alt={meal.title}
                className="summary__meal-img"
              />
              <div className="summary__meal-info">
                <p className="summary__meal-title">{meal.title}</p>
                {group.side && (
                  <p className="summary__meal-side">{group.side}</p>
                )}
              </div>
              <p className="summary__meal-price">
                {formatPrice(MEAL_PRICE * group.count)}
              </p>
              <div className="summary__stepper">
                <button
                  className="summary__stepper-btn"
                  onClick={() =>
                    onDecrementCombo(meal.id, group.side, group.size)
                  }
                  aria-label="Retirer"
                >
                  <img src={group.count > 1 ? iconMinus : iconTrash} alt="" />
                </button>
                <span className="summary__stepper-qty">{group.count}</span>
                <button
                  className="summary__stepper-btn"
                  onClick={() =>
                    onIncrementCombo(meal.id, group.side, group.size)
                  }
                  aria-label="Ajouter"
                >
                  <img src={iconPlus} alt="" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Discount info */}
        <div className="summary__promo-section">
          <p className="summary__section-title">Rabais première commande</p>
          <div className="summary__promo-row summary__promo-row--active">
            <div className="summary__promo-order">
              <span className="summary__promo-ordinal">
                1<sup>ère</sup>
              </span>
              <img src={iconBoxCheck} alt="" className="summary__promo-box" />
            </div>
            <span className="summary__promo-label">20 $ de rabais</span>
          </div>
          <div className="summary__promo-row">
            <div className="summary__promo-order">
              <span className="summary__promo-ordinal">
                2<sup>ème</sup>
              </span>
              <img src={iconBox} alt="" className="summary__promo-box" />
            </div>
            <span className="summary__promo-label">20 $ de rabais</span>
          </div>
          <div className="summary__promo-row">
            <div className="summary__promo-order">
              <span className="summary__promo-ordinal">
                3<sup>ème</sup>
              </span>
              <img src={iconBox} alt="" className="summary__promo-box" />
            </div>
            <span className="summary__promo-label">20 $ de rabais</span>
          </div>
        </div>

        {/* Invoice */}
        <div className="summary__invoice">
          <div className="summary__invoice-row">
            <span>Sous total</span>
            <span>{formatPrice(subtotal)}</span>
          </div>
          <div className="summary__invoice-row summary__invoice-row--discount">
            <span>Rabais</span>
            <span>-{formatPrice(DISCOUNT)}</span>
          </div>
          <div className="summary__invoice-row">
            <span>Livraison</span>
            <span>{formatPrice(DELIVERY_FEE)}</span>
          </div>
          <div className="summary__invoice-row summary__invoice-row--tax">
            <span>TPS (5%)</span>
            <span>{formatPrice(0)}</span>
          </div>
          <div className="summary__invoice-row summary__invoice-row--tax">
            <span>TVQ (9,975%)</span>
            <span>{formatPrice(0)}</span>
          </div>
          <div className="summary__invoice-total">
            <span>TOTAL</span>
            <span>{formatPrice(total)}</span>
          </div>
        </div>

        {/* Confirm */}
        <div className="summary__footer">
          <button
            className={`summary__confirm${isComplete ? " summary__confirm--active" : ""}`}
            onClick={onConfirm}
            disabled={!isComplete}
          >
            Confirmer
          </button>
          <p className="summary__min-note">
            *Un minimum de {TOTAL_SLOTS} repas par commande est requis.
          </p>
        </div>
      </div>
    </>
  );
}
